import { ChevronDown, TrendingUp } from 'lucide-react'
import { useMemo, useState } from 'react'
import { LineChart } from '../../components/LineChart'
import { useStrata } from '../../data/StrataProvider'
import { formatNumber } from '../../domain/progression'
import { displayMass, massUnit } from '../../domain/units'

type Metric = 'Carga' | 'Reps' | 'Volumen'

export function ProgressPage() {
  const { state } = useStrata()
  const defaultExercise = state.exercises.find((exercise) => exercise.id === 'incline-press') ?? state.exercises[0]
  const [scope, setScope] = useState<'exercise' | 'body'>('exercise')
  const [exerciseId, setExerciseId] = useState(defaultExercise.id)
  const [metric, setMetric] = useState<Metric>('Carga')
  const exercise = state.exercises.find((item) => item.id === exerciseId) ?? defaultExercise

  const points = useMemo(() => [...state.sessions]
    .filter((session) => session.status !== 'in_progress')
    .sort((a, b) => a.startedAt.localeCompare(b.startedAt))
    .flatMap((session) => {
      const sets = session.sets.filter((set) => set.exerciseId === exercise.id)
      if (!sets.length) return []
      const value = metric === 'Carga'
        ? Math.max(0, ...sets.map((set) => set.weight ?? 0))
        : metric === 'Reps'
          ? Math.max(0, ...sets.map((set) => set.reps ?? 0))
          : sets.reduce((sum, set) => sum + (set.weight ?? 0) * (set.reps ?? 0), 0)
      return [{ label: shortDate(session.startedAt), value: metric === 'Reps' ? value : displayMass(value, state.preferences.unit) }]
    }), [state.sessions, state.preferences.unit, exercise.id, metric])
  const latest = points.at(-1)?.value ?? 0
  const previous = points.at(-2)?.value ?? 0
  const delta = previous ? ((latest - previous) / previous) * 100 : 0
  const bodyPoints = [...state.measurements].sort((a, b) => a.date.localeCompare(b.date)).map((item) => ({ label: shortDate(item.date), value: displayMass(item.weight, state.preferences.unit) }))
  const unit = massUnit(state.preferences.unit)

  return (
    <div className="page progress-page">
      <header className="page-header"><span className="eyebrow">TENDENCIA</span><h1>Progreso</h1></header>
      <div className="segmented"><button className={scope === 'exercise' ? 'active' : ''} onClick={() => setScope('exercise')}>Ejercicio</button><button className={scope === 'body' ? 'active' : ''} onClick={() => setScope('body')}>Peso corporal</button></div>

      {scope === 'exercise' ? (
        <section className="progress-content">
          <label className="exercise-select"><select value={exerciseId} onChange={(event) => setExerciseId(event.target.value)}>{state.exercises.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select><ChevronDown size={16} /></label>
          <div className="progress-value"><strong>{metric === 'Volumen' ? `${formatNumber(latest)} ${unit}` : `${formatNumber(latest)} ${metric === 'Carga' ? unit : 'reps'}`}</strong>{delta !== 0 && <span><TrendingUp size={14} />{Math.abs(delta).toFixed(1)}%</span>}</div>
          <div className="segmented three">{(['Carga', 'Reps', 'Volumen'] as Metric[]).map((item) => <button key={item} className={metric === item ? 'active' : ''} onClick={() => setMetric(item)}>{item}</button>)}</div>
          <LineChart points={points} suffix={metric === 'Carga' || metric === 'Volumen' ? ` ${unit}` : ' reps'} />
          <div className="quick-stats"><div><span>Mejor</span><strong>{formatNumber(Math.max(0, ...points.map((item) => item.value)))}</strong></div><div><span>Sesiones</span><strong>{points.length}</strong></div><div><span>Tendencia</span><strong>{delta >= 0 ? '+' : ''}{delta.toFixed(1)}%</strong></div></div>
        </section>
      ) : (
        <section className="progress-content">
          <div className="progress-value"><strong>{formatNumber(bodyPoints.at(-1)?.value ?? 0)} {unit}</strong>{bodyPoints.length > 1 && <span>{((bodyPoints.at(-1)?.value ?? 0) - bodyPoints[0].value >= 0 ? '+' : '')}{formatNumber((bodyPoints.at(-1)?.value ?? 0) - bodyPoints[0].value)} {unit}</span>}</div>
          <LineChart points={bodyPoints} suffix={` ${unit}`} />
          <p className="support-copy">El peso corporal se muestra como tendencia; un cambio diario aislado no modifica tus objetivos de entrenamiento.</p>
        </section>
      )}
    </div>
  )
}

const shortDate = (value: string) => new Intl.DateTimeFormat('es-MX', { day: 'numeric', month: 'short' }).format(new Date(value))
